import React from 'react';
import { IconButton, IconButtonProps, styled, Theme, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { Props } from './icon-button.types';

const LwIconButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const theme = useTheme();
  const sxOverride = getSxOverride(props, theme);
  const component = getComponent(props);
  const { iconColorMode, ...rest } = props;

  return <StyledIconButton as={component} sx={sxOverride} ref={ref} {...rest} />;
});

const StyledIconButton = styled(IconButton)<IconButtonProps>(({ theme, size }) => ({
  '&.MuiIconButton-root': {
    borderRadius: '50%',
    boxShadow: 'none',
    height: getButtonSize(size),
    width: getButtonSize(size),
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'none',
      opacity: 0.4,
    },
  },

  '&.MuiIconButton-colorPrimary': {
    backgroundColor: theme.palette.lwWhite[100],
    border: `1px solid ${theme.palette.lwPrimaryAccentDark[20]}`,

    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwPrimaryAccentDark[10]}`,
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.lwPrimaryAccentLight[20],
    },
  },

  '&.MuiIconButton-colorSecondary': {
    backgroundColor: theme.palette.lwWhite[100],
    border: `1px solid ${theme.palette.lwPrimaryAccentDark[20]}`,

    '&:hover': {
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
      boxShadow: `0px 8px 24px ${theme.palette.lwPrimaryAccentDark[20]}`,
    },

    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
    },

    '&:active': {
      backgroundColor: theme.palette.lwPrimaryAccentDark[10],
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[100]}`,

      '&:focus': {
        boxShadow: 'none',
      },
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.lwWhite[100],
      border: `1px solid ${theme.palette.lwPrimaryAccentDark[10]}`,
    },
  },
}));

const getSxOverride = ({ iconColorMode, onClick, to }: Props, theme: Theme) => ({
  '&.MuiIconButton-root': {
    ...(!onClick && !to
      ? {
          pointerEvents: 'none',
        }
      : {}),
  },
  '&.MuiIconButton-colorPrimary': {
    '& svg path': {
      [iconColorMode]: theme.palette.lwBlack[100],
    },
    '&:active svg path': {
      [iconColorMode]: theme.palette.lwWhite[80],
    },
  },

  '&.MuiIconButton-colorSecondary': {
    '&:hover svg path': {
      [iconColorMode]: theme.palette.lwPrimaryAccentLight[100],
    },

    '&:active svg path': {
      [iconColorMode]: theme.palette.lwPrimaryAccentDark[100],
    },

    '&.Mui-disabled': {
      '& svg path': {
        [iconColorMode]: theme.palette.lwPrimaryAccentDark[40],
      },
    },

    '& svg path': {
      [iconColorMode]: theme.palette.lwPrimaryAccentDark[100],
    },
  },

  '&.MuiIconButton-colorError': {
    '& svg path': {
      [iconColorMode]: theme.palette.error.main,
    },

    '&:active svg path': {
      [iconColorMode]: theme.palette.error.dark,
    },

    '&.Mui-disabled': {
      '& svg path': {
        [iconColorMode]: theme.palette.error.light,
      },
    },
  },
});

const getComponent = (props: Props) => {
  if (props.to) {
    return Link;
  }

  if (props.onClick) {
    return IconButton;
  }

  return 'span';
};

const getButtonSize = (size: 'large' | 'medium' | 'small' | undefined) => {
  switch (size) {
    case 'small':
      return '24px';
    case 'medium':
      return '40px';
    case 'large':
    default:
      return '56px';
  }
};

export { LwIconButton };
